import { FC, useState, useCallback, useMemo } from 'react';
import {
  Box,
  InputLabel,
  Select,
  MenuItem,
  Button,
  SelectChangeEvent,
  Typography,
  CircularProgress,
} from '@mui/material';
import { format } from 'date-fns';
import ReusableModal from '../common/Modal';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useMutation } from '@tanstack/react-query';
import { scheduleUvOutboundCallApi } from '../../utils/apis';
import { useTimeZones } from '../../hooks/UseTimeZones';
import { useAgents } from '../../hooks/useAgents';
import theme from '../../theme';
import { IScheduleUvOutboundCall } from '../../schemas/apis/interface';
import PhoneInput from 'react-phone-input-2';

interface ScheduleCallsModalProps {
  open: boolean;
  onClose: () => void;
  selectedRows: string[];
  agentId: string;
  agentKey: string;
  onSuccess?: () => void;
  onError?: (errorMessage: string) => void;
}

const ScheduleCallsModal: FC<ScheduleCallsModalProps> = ({
  open,
  onClose,
  selectedRows,
  agentId,
  agentKey,
  onSuccess,
  onError,
}) => {
  const [formData, setFormData] = useState({
    startDate: null as Date | null,
    endDate: null as Date | null,
    startTime: null as Date | null,
    endTime: null as Date | null,
    selectedTimezone: 'Asia/Kolkata' as string,
    agentId: agentId,
    phoneNumber: '' as string,
    countryCode: '91' as string,
    provider: 'twilio' as string,
    distribution: 'even' as string,
  });

  const [errors, setErrors] = useState({
    startDate: false,
    endDate: false,
    startTime: false,
    endTime: false,
    phoneNumber: false,
    countryCode: false,
  });

  const { timeZones } = useTimeZones();
  const { agents } = useAgents();

  // Get current agent info
  const currentAgent = useMemo(() => {
    return agents?.agents?.find((agent: any) => agent._id === agentId);
  }, [agents, agentId]);

  const phoneNumbersData = useMemo(() => {
    const assignedNumbers = currentAgent?.assignedNumbers;
    if (!assignedNumbers) return [];

    return Object.entries(assignedNumbers).flatMap(
      ([provider, phoneNumbers]) => {
        const numbers = Array.isArray(phoneNumbers)
          ? phoneNumbers
          : [phoneNumbers];
        return numbers
          .filter(
            phoneNumber =>
              typeof phoneNumber === 'string' && phoneNumber.trim() !== ''
          )
          .map(phoneNumber => ({
            provider,
            phoneNumber,
          }));
      }
    );
  }, [currentAgent]);

  const assignedPhoneNumbers = useMemo(() => {
    return phoneNumbersData.map(p => p.phoneNumber);
  }, [phoneNumbersData]);

  const handleSelectChange = (field: string) => (event: SelectChangeEvent) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
    setErrors(prev => ({
      ...prev,
      ...(field === 'phoneNumber' && { phoneNumber: false }),
      ...(field === 'countryCode' && { countryCode: false }),
    }));
  };

  const handlePhoneNumberChange = (event: SelectChangeEvent) => {
    const selectedPhoneNumber = event.target.value;
    const providerForNumber = phoneNumbersData.find(
      p => p.phoneNumber === selectedPhoneNumber
    )?.provider;

    setFormData(prev => ({
      ...prev,
      phoneNumber: selectedPhoneNumber,
      ...(providerForNumber ? { provider: providerForNumber } : {}),
    }));

    setErrors(prev => ({
      ...prev,
      phoneNumber: false,
    }));
  };

  const { mutate: scheduleUvOutboundCall, isPending } = useMutation({
    mutationFn: scheduleUvOutboundCallApi,
    onSuccess: useCallback(() => {
      onClose();
      onSuccess?.();
    }, [onClose, onSuccess]),
    onError: useCallback(
      (error: any) => {
        const errorMessage =
          error?.response?.data?.message ||
          'Failed to schedule calls. Please try again.';
        onError?.(errorMessage);
      },
      [onError]
    ),
  });

  const handleSubmit = useCallback(() => {
    if (selectedRows.length === 0) {
      onError?.(
        'Selected records are not ready yet. Please wait for the records to finish processing.'
      );
      return;
    }

    const newErrors = {
      startDate: !formData.startDate,
      endDate: !formData.endDate,
      startTime: !formData.startTime,
      endTime: !formData.endTime,
      phoneNumber: !formData.phoneNumber,
      countryCode: !formData.countryCode,
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some(error => error)) {
      return;
    }

    const formatDate = (date: Date | null) => {
      if (!date) return '';
      return format(date, 'yyyy-MM-dd');
    };

    const formatTime = (time: Date | null) => {
      if (!time) return '';
      return time.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    };

    // Get provider from selected phone number
    const selectedPhoneNumberData = phoneNumbersData.find(
      p => p.phoneNumber === formData.phoneNumber
    );

    const formattedData = {
      startDate: formatDate(formData.startDate),
      endDate: formatDate(formData.endDate),
      startTime: formatTime(formData.startTime),
      endTime: formatTime(formData.endTime),
      selectedTimezone: formData.selectedTimezone,
      distribution: formData.distribution,
      selectedRowsIds: selectedRows,
      agentId: agentId,
      provider: selectedPhoneNumberData?.provider || formData.provider,
      phoneNumber: formData.phoneNumber,
      countryCode: formData.countryCode ? `+${formData.countryCode}` : '',
      key: agentKey,
    };

    scheduleUvOutboundCall(formattedData as IScheduleUvOutboundCall);
  }, [
    scheduleUvOutboundCall,
    formData,
    selectedRows,
    agentId,
    agentKey,
    phoneNumbersData,
    onError,
  ]);

  const commonSelectStyles = useMemo(
    () => ({
      height: '40px',
      '& .MuiSelect-select': {
        display: 'flex',
        padding: '8px 14px',
        alignItems: 'center',
        borderRadius: '6px',
        border: '1px solid #DCE0E7',
        background: '#F8F9FC',
        fontSize: '14px',
        fontWeight: 400,
        lineHeight: 'normal',
        letterSpacing: '-0.14px',
        color: theme.palette.lightGray.text,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none',
      },
    }),
    []
  );

  const commonInputStyles = useMemo(
    () => ({
      bgcolor: '#F4F3FF',
      borderRadius: '6px',
      height: '40px',
      '& .MuiOutlinedInput-root': {
        height: '40px',
        '& fieldset': {
          border: 'none',
        },
      },
      '& input': {
        fontSize: '14px',
        fontWeight: 400,
        padding: '8px 14px',
      },
    }),
    []
  );

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <ReusableModal
        open={open}
        onClose={onClose}
        title="Schedule Outbound Calls"
        width={600}
      >
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              {/* Start Date */}
              <Box>
                <InputLabel sx={{ mb: 1, fontSize: '14px' }}>
                  Start Date
                </InputLabel>
                <DatePicker
                  value={formData.startDate}
                  format="dd-MM-yyyy"
                  onChange={newValue => {
                    setFormData(prev => ({
                      ...prev,
                      startDate: newValue,
                    }));
                    setErrors(prev => ({ ...prev, startDate: false }));
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      ...commonInputStyles['& .MuiOutlinedInput-root'],
                      bgcolor: '#F4F3FF',
                      border: errors.startDate ? '1px solid #d32f2f' : 'none',
                    },
                  }}
                />
                {errors.startDate && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                    Required
                  </Typography>
                )}
              </Box>

              {/* Start Time and End Time */}
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <InputLabel sx={{ mb: 1, fontSize: '14px' }}>
                    Start Time
                  </InputLabel>
                  <TimePicker
                    value={formData.startTime}
                    onChange={newValue => {
                      setFormData(prev => ({
                        ...prev,
                        startTime: newValue,
                      }));
                      setErrors(prev => ({ ...prev, startTime: false }));
                    }}
                    ampm={false}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        ...commonInputStyles['& .MuiOutlinedInput-root'],
                        bgcolor: '#F4F3FF',
                        border: errors.startTime ? '1px solid #d32f2f' : 'none',
                      },
                    }}
                  />
                  {errors.startTime && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ mt: 0.5 }}
                    >
                      Required
                    </Typography>
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <InputLabel sx={{ mb: 1, fontSize: '14px' }}>
                    End Time
                  </InputLabel>
                  <TimePicker
                    value={formData.endTime}
                    onChange={newValue => {
                      setFormData(prev => ({
                        ...prev,
                        endTime: newValue,
                      }));
                      setErrors(prev => ({ ...prev, endTime: false }));
                    }}
                    ampm={false}
                    sx={{
                      width: '100%',
                      '& .MuiOutlinedInput-root': {
                        ...commonInputStyles['& .MuiOutlinedInput-root'],
                        bgcolor: '#F4F3FF',
                        border: errors.endTime ? '1px solid #d32f2f' : 'none',
                      },
                    }}
                  />
                  {errors.endTime && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ mt: 0.5 }}
                    >
                      Required
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Country Code */}
              <Box>
                <InputLabel sx={{ mb: 1, fontSize: '14px' }}>
                  Country Code
                </InputLabel>
                <Box
                  sx={{
                    '& .form-control': {
                      width: '100%',
                    },
                    '& .flag-dropdown': {
                      border: '1px solid #DCE0E7',
                      borderRadius: '6px 0 0 6px',
                    },
                    '& .form-control input': {
                      height: '40px',
                      borderRadius: '0 6px 6px 0',
                    },
                  }}
                >
                  <PhoneInput
                    country={'in'}
                    enableSearch
                    countryCodeEditable={false}
                    value={formData.countryCode}
                    onChange={(_, country: any) => {
                      setFormData(prev => ({
                        ...prev,
                        countryCode: country?.dialCode || '',
                      }));
                      setErrors(prev => ({
                        ...prev,
                        countryCode: false,
                      }));
                    }}
                    specialLabel=""
                    inputStyle={{
                      width: '100%',
                      height: '40px',
                      fontSize: '14px',
                    }}
                    containerStyle={{
                      width: '100%',
                    }}
                  />
                </Box>
                {errors.countryCode && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                    Required
                  </Typography>
                )}
              </Box>
              {/* Phone Numbers */}
              <Box>
                <InputLabel sx={{ mb: 1, fontSize: '14px' }}>
                  Phone Numbers
                </InputLabel>
                <Select
                  fullWidth
                  value={formData.phoneNumber}
                  onChange={handlePhoneNumberChange}
                  sx={{
                    ...commonSelectStyles,
                    border: errors.phoneNumber ? '1px solid #d32f2f' : 'none',
                  }}
                  displayEmpty
                  disabled={assignedPhoneNumbers.length === 0}
                >
                  <MenuItem value="" disabled>
                    {assignedPhoneNumbers.length === 0
                      ? 'No phone numbers assigned'
                      : 'Select a phone number'}
                  </MenuItem>
                  {assignedPhoneNumbers.map(phoneNumber => (
                    <MenuItem key={phoneNumber} value={phoneNumber}>
                      {phoneNumber}
                    </MenuItem>
                  ))}
                </Select>
                {errors.phoneNumber && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                    Required
                  </Typography>
                )}
                {assignedPhoneNumbers.length === 0 && (
                  <Typography
                    color="warning.main"
                    variant="caption"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    A phone number should be assigned to the agent for
                    scheduling calls
                  </Typography>
                )}
              </Box>
            </Box>

            <Box
              sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 3 }}
            >
              {/* End Date */}
              <Box>
                <InputLabel sx={{ mb: 1, fontSize: '14px' }}>
                  End Date
                </InputLabel>
                <DatePicker
                  value={formData.endDate}
                  format="dd-MM-yyyy"
                  onChange={newValue => {
                    setFormData(prev => ({
                      ...prev,
                      endDate: newValue,
                    }));
                    setErrors(prev => ({ ...prev, endDate: false }));
                  }}
                  sx={{
                    width: '100%',
                    '& .MuiOutlinedInput-root': {
                      ...commonInputStyles['& .MuiOutlinedInput-root'],
                      bgcolor: '#F4F3FF',
                      border: errors.endDate ? '1px solid #d32f2f' : 'none',
                    },
                  }}
                />
                {errors.endDate && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                    Required
                  </Typography>
                )}
              </Box>

              {/* Timezone */}
              <Box>
                <InputLabel sx={{ mb: 1, fontSize: '14px' }}>
                  Timezone
                </InputLabel>
                <Select
                  fullWidth
                  value={formData.selectedTimezone}
                  onChange={handleSelectChange('selectedTimezone')}
                  sx={commonSelectStyles}
                >
                  {timeZones.map((timezone: string) => (
                    <MenuItem key={timezone} value={timezone}>
                      {timezone}
                    </MenuItem>
                  ))}
                </Select>
              </Box>

              {/* Distribution */}
              <Box>
                <InputLabel sx={{ mb: 1, fontSize: '14px' }}>
                  Distribution
                </InputLabel>
                <Select
                  fullWidth
                  value={formData.distribution}
                  onChange={handleSelectChange('distribution')}
                  sx={commonSelectStyles}
                >
                  <MenuItem value="even">Even</MenuItem>
                  <MenuItem value="random">Random</MenuItem>
                </Select>
              </Box>
            </Box>
          </Box>

          {/* Schedule Call Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={
                isPending ||
                assignedPhoneNumbers.length === 0 ||
                selectedRows.length === 0
              }
              sx={{
                textTransform: 'none',
                borderRadius: '6px',
                height: '40px',
                width: '150px',
                position: 'relative',
              }}
            >
              {isPending ? (
                <CircularProgress
                  size={24}
                  sx={{
                    color: 'white',
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    marginTop: '-12px',
                    marginLeft: '-12px',
                  }}
                />
              ) : (
                'Schedule Call'
              )}
            </Button>
          </Box>
        </Box>
      </ReusableModal>
    </LocalizationProvider>
  );
};

export default ScheduleCallsModal;
